import { useCallback } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateDefaultEntitlement,
  useAdminGetProductDefaultEntitlements,
  getAdminGetProductDefaultEntitlementsQueryKey,
} from '@/api/auth/admin/admin';
import { useAdminCreateDefaultEntitlement } from '@/api/auth/admin/admin';
import type { DefaultEntitlementResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EntitlementRuleFields } from '@/components/admin/EntitlementRuleFields';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  createDefaultEntitlementSchema,
  UpdateDefaultEntitlement,
  type CreateDefaultEntitlement,
} from '@/schemas/default-entitlement';
import type { EntitlementRule } from '@/api/auth/missionImpactAuthAPI.schemas';
import { getAdminGetProductsQueryKey } from '@/api/auth/admin/admin';
import { logger } from '@/services/logger';

/**
 * Typed default entitlement with properly parsed entitlement rule
 */
interface TypedDefaultEntitlement extends Omit<DefaultEntitlementResponse, 'rule'> {
  rule?: EntitlementRule;
}

/**
 * Props for the DefaultEntitlementForm component
 */
interface DefaultEntitlementFormProps {
  /** The product ID */
  productId: string;
}

/**
 * Form component for creating or updating a default entitlement
 */
export function DefaultEntitlementForm({ productId }: DefaultEntitlementFormProps) {
  const queryClient = useQueryClient();

  // Memoize the selector function to prevent unnecessary re-renders
  const selectTypedEntitlement = useCallback((data: any): TypedDefaultEntitlement | undefined => {
    if (!data?.data) return undefined;

    // The API returns a typed, polymorphic EntitlementRule directly.
    return data.data as DefaultEntitlementResponse;
  }, []);

  // Fetch existing entitlements for this product
  const {
    data: existingEntitlement,
    isLoading: isLoadingEntitlements,
    error: entitlementsError,
  } = useAdminGetProductDefaultEntitlements(productId, {
    query: {
      enabled: !!productId,
      select: selectTypedEntitlement,
    },
  });

  // Check if error is a 404 (no entitlement exists yet)
  const is404 = entitlementsError?.response?.status === 404;
  const isEditing = !!existingEntitlement && !is404;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CreateDefaultEntitlement | UpdateDefaultEntitlement>({
    // The schema validates the generated polymorphic rule; cast bridges the resolver's input type.
    resolver: zodResolver(createDefaultEntitlementSchema) as Resolver<
      CreateDefaultEntitlement | UpdateDefaultEntitlement
    >,
    defaultValues: {
      rule: existingEntitlement?.rule ?? { type: 'cumulative', count: 1 },
      durationDays: existingEntitlement?.durationDays || undefined,
    },
  });

  const { mutate: createEntitlement } = useAdminCreateDefaultEntitlement({
    mutation: {
      onSuccess: () => {
        toast.success('Default entitlement created successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductDefaultEntitlementsQueryKey(productId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductsQueryKey(),
        });
      },
      onError: (error) => {
        logger.error(error, {
          context: 'CreateDefaultEntitlement',
          productId,
        });
        toast.error(error.response?.data?.detail || 'Failed to create default entitlement');
      },
    },
  });

  const { mutate: updateEntitlement } = useAdminUpdateDefaultEntitlement({
    mutation: {
      onSuccess: () => {
        toast.success('Default entitlement updated successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductDefaultEntitlementsQueryKey(productId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductsQueryKey(),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'UpdateDefaultEntitlement',
          productId,
        });
        toast.error(
          error.response?.data?.detail || 'Failed to update default entitlement.'
        );
      },
    },
  });

  const onSubmit = useCallback(
    (data: CreateDefaultEntitlement | UpdateDefaultEntitlement) => {
      // Transform null to undefined for API compatibility
      const transformedData = {
        ...data,
        durationDays: data.durationDays ?? undefined,
      };

      if (isEditing && existingEntitlement) {
        updateEntitlement({
          productId,
          data: transformedData,
        });
      } else {
        createEntitlement({
          productId,
          data: transformedData,
        });
      }
    },
    [isEditing, existingEntitlement, updateEntitlement, createEntitlement, productId]
  );

  // Handle loading state
  if (isLoadingEntitlements) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
          <Skeleton className="h-4 w-[300px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-[150px]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle error state (but not 404s, which just means no entitlement exists yet)
  if (entitlementsError && !is404) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Default Entitlement</CardTitle>
          <CardDescription>Failed to load entitlements</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-destructive py-8">
            <p>
              Failed to load entitlements: {entitlementsError.response?.data?.detail || 'Unknown error'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEditing ? 'Edit' : 'Create'} Default Entitlement</CardTitle>
        <CardDescription>
          Configure the default entitlement that will be granted to new users
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <EntitlementRuleFields
            setValue={setValue}
            watch={watch}
            fieldPath="rule"
            error={errors.rule?.message}
          />

          <div className="grid gap-2">
            <Label htmlFor="durationDays">Duration in Days (Optional)</Label>
            <Input
              id="durationDays"
              type="number"
              min="1"
              placeholder="30, 90, 365, or leave empty for perpetual"
              {...register('durationDays', {
                setValueAs: (value) => (value === '' || isNaN(value) ? undefined : Number(value)),
              })}
            />
            {errors.durationDays && (
              <p className="text-sm text-destructive">{errors.durationDays.message}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Number of days for the entitlement. Examples: 30 (30 days), 365 (1 year).
              Leave empty for perpetual entitlement.
            </p>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting || (isEditing && !isDirty)}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'} Entitlement
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
